import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, TextInput, Switch, Box } from '@mantine/core';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DateField, TimeField } from '@components/shared';
import { CustomTooltip } from '@components/shared';
import { todayStart, dayStart, dayEnd } from '@util/dates';
import { initialState as initialKeywordSettings } from '@components/Keyword/KeywordSettings/helpers';
import { locationVariety } from '@components/Location/helpers';
import { keywordWizardSettings } from './helpers';

export const defaultFormState = {
  allow_sms_claim: true,
  end_date: '',
  end_time: dayEnd,
  is_staff: false,
  keyword: '',
  lock_to_location: false,
  start_date: todayStart,
  start_time: dayStart,
};

export default function KeywordForm({
  additionalReqData = {},
  campaignId,
  contestId,
  editability = {
    keyword: true,
    start_date_and_time: true,
    end_date_and_time: true,
  },
  effortId,
  formInfo = defaultFormState,
  id,
  locationId,
  organizationId,
  minStartDate = null,
  maxEndDate = null,
  onSuccess,
  smsRequired = false,
  wizard = false,
}) {
  const [formData, setFormData] = useState(defaultFormState);
  const [keywordAvailable, setKeywordAvailable] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const adminInfo = useSelector((state) => state.admin);
  const { t } = useTranslation();
  const managerInfo = useSelector((state) => state.manager);
  const isLocationManager = managerInfo?.location_id ? true : false;
  const isOrgManager = managerInfo?.organization_id ? true : false;
  const isDealerLocation = !managerInfo
    ? false
    : managerInfo.location_variety === locationVariety.dealer;

  useEffect(() => {
    setFormData({
      ...formData,
      ...formInfo,
    });
  }, [JSON.stringify(formInfo)]);

  const CancelToken = axios.CancelToken;
  const cancelRef = useRef(null);

  const getEntityInfo = () => {
    const req = {};

    if (effortId) req.campaign_effort_id = effortId;
    if (campaignId) req.campaign_id = campaignId;
    if (contestId) req.contest_id = contestId;
    if (locationId) req.location_id = locationId;
    if (organizationId) req.organization_id = organizationId;
    if (id) req.keyword_id = id;

    if (isLocationManager) {
      delete req.organization_id;

      if (!locationId) req.location_id = managerInfo.location_id;
    }

    if (isOrgManager) {
      if (req.location_id || additionalReqData?.location_id) {
        delete req.organization_id;
      } else {
        req.organization_id = managerInfo.organization_id;
      }
    }

    return req;
  };

  useEffect(() => {
    if (!formData.keyword) {
      setKeywordAvailable(true);
    }
    verifyKeyword();
  }, [JSON.stringify(formData)]);

  useEffect(() => {
    if (smsRequired) {
      setFormData({
        ...formData,
        allow_sms_claim: true,
      });
    }
  }, [smsRequired]);

  useEffect(() => {
    verifyKeyword();
  }, [organizationId, locationId, effortId, campaignId]);

  function verifyKeyword() {
    if (id) return;
    if (
      !formData.keyword ||
      !formData.start_date ||
      !formData.end_date ||
      !formData.start_time ||
      !formData.end_time
    )
      return;

    const cancel = cancelRef.current;
    if (cancel) {
      cancel();
    }

    const req = {
      ...additionalReqData,
      ...formData,
      ...getEntityInfo(),
    };

    axios
      .post(`/keyword-search/`, req, {
        cancelToken: new CancelToken(function executor(c) {
          cancelRef.current = c;
        }),
      })
      .then(({ data }) => {
        setKeywordAvailable(data.response[0].available);
      })
      .catch((err) => {
        setKeywordAvailable(false);
      });
  }

  function onSubmit() {
    if (!formData.keyword) return;

    if (id) return onUpdate();

    setSubmitting(true);

    const req = {
      ...additionalReqData,
      ...formData,
      ...getEntityInfo(),
    };

    if (!req.settings) req.settings = initialKeywordSettings;
    if (isDealerLocation) req.allow_sms_claim = false;

    // if hash is missing wizard settings, add them
    if (!req.wizard_settings) {
      req.wizard_settings = keywordWizardSettings;
    }

    if (wizard) {
      return onWizardSubmit(req);
    }

    axios
      .post(`/keywords/`, req)
      .then(({ data }) => {
        setSubmitting(false);
        toast.success(t('common:added') + '!');
        setFormData(defaultFormState);
        onSuccess(data.response[0].id, formData.keyword);
      })
      .catch((err) => {
        toast.error(err);
        setSubmitting(false);
      });
  }

  function onWizardSubmit(reqData) {
    const req = {
      ...getEntityInfo(),
      create_keyword: {
        ...reqData,
        create_effort: {
          ...reqData.create_effort,
          title: `${reqData.keyword} Points Keyword Effort`,
        },
      },
    };

    // if hash is missing wizard settings, add them
    if (!req.wizard_settings) {
      req.wizard_settings = keywordWizardSettings;
    }

    axios
      .post(`/wizard/`, req)
      .then(({ data }) => {
        // onSuccess(data.response[0].keyword_id);
        onSuccess(data.response[0].campaign_effort_id);
      })
      .catch((err) => {
        setSubmitting(false);
        toast.error(err);
      });
  }

  function onUpdate() {
    if (!formData.keyword) return;

    setSubmitting(true);

    const req = {
      ...getEntityInfo(),
      ...formData,
    };

    axios
      .put(`/keywords/${id}/`, req)
      .then(() => {
        setSubmitting(false);
        toast.success(t('common:updated') + '!');
        onSuccess();
      })
      .catch((err) => {
        toast.error(err);
        setSubmitting(false);
      });
  }

  return (
    <div>
      {!id && (
        <TextInput
          value={formData.keyword}
          maxLength={100}
          onChange={(e) =>
            setFormData({ ...formData, keyword: e.target.value })
          }
          label={t('mixer:keyword_singular')}
          error={
            id
              ? false
              : !keywordAvailable
              ? t('mixer:keyword:keyword_unavailable', {
                  keyword: formData.keyword,
                })
              : false
          }
          disabled={!editability.keyword || id ? true : false}
        />
      )}
      <DateField
        value={formData.start_date}
        onChange={(e) => setFormData({ ...formData, start_date: e })}
        label={`${t('common:start_date')}${
          minStartDate ? ` (min ${minStartDate})` : ''
        }`}
        min={minStartDate}
        disabled={!editability.start_date_and_time}
      />
      <TimeField
        value={formData.start_time}
        onChange={(e) => setFormData({ ...formData, start_time: e })}
        label={t('common:start_time')}
        disabled={!editability.start_date_and_time}
      />
      <DateField
        value={formData.end_date}
        onChange={(e) => setFormData({ ...formData, end_date: e })}
        label={`${t('common:end_date')}${
          maxEndDate ? ` (max ${maxEndDate})` : ''
        }`}
        min={formData.start_date}
        max={maxEndDate}
        disabled={!editability.end_date_and_time}
      />
      <TimeField
        value={formData.end_time}
        onChange={(e) => setFormData({ ...formData, end_time: e })}
        label={t('common:end_time')}
        disabled={!editability.end_date_and_time}
      />
      {id && (
        <Box my="md">
          <CustomTooltip label={t('mixer:keyword:staff_keyword_tooltip')}>
            <Switch
              label={t('mixer:keyword:staff_keyword')}
              checked={formData.is_staff}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  is_staff: !formData.is_staff,
                })
              }
            />
          </CustomTooltip>
        </Box>
      )}
      {locationId && (
        <Box my="md">
          <CustomTooltip label={t('mixer:keyword:lock_to_location_tooltip')}>
            <Switch
              label={t('mixer:keyword:lock_to_location')}
              checked={formData.lock_to_location}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  lock_to_location: !formData.lock_to_location,
                })
              }
            />
          </CustomTooltip>
        </Box>
      )}
      {!isDealerLocation && !wizard && !smsRequired && (
        <Switch
          label={t('mixer:keyword:allow_sms_claim')}
          disabled={id ? true : false}
          mt="xs"
          checked={formData.allow_sms_claim}
          onChange={(e) =>
            setFormData({
              ...formData,
              allow_sms_claim: !formData.allow_sms_claim,
            })
          }
        />
      )}
      <Button
        onClick={onSubmit}
        loading={submitting}
        mt="lg"
        fullWidth
        disabled={
          submitting ||
          !keywordAvailable ||
          !formData.keyword ||
          !formData.start_date ||
          !formData.end_date ||
          !formData.start_time ||
          !formData.end_time
        }
      >
        {wizard
          ? t('common:create')
          : `${id ? t('common:update') : t('common:add')}`}
      </Button>
    </div>
  );
}
